import React, { useState, useEffect, useRef } from 'react';
import { Trash2, Plus, AlertCircle, CheckCircle2, Mail, Key, RefreshCw, UserCheck, Download, Upload, Search } from 'lucide-react';
import { API_BASE, apiFetch } from '../config';
import { useLanguage } from '../i18n';
import { MappingSkeleton } from './Skeleton';

const Mappings = () => {
  const { t } = useLanguage();
  const [mappings, setMappings] = useState([]);
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState('');
  const [chatId, setChatId] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingEmail, setDeletingEmail] = useState(null);
  const [result, setResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const importInputRef = useRef(null);

  const fetchData = async () => {
    setLoading(true);
    setLoadingUsers(true);
    try {
      const [mappingsRes, usersRes] = await Promise.all([
        apiFetch(`${API_BASE}/api/mappings`),
        apiFetch(`${API_BASE}/api/users`)
      ]);
      
      if (mappingsRes.ok) {
        const mappingsData = await mappingsRes.json();
        setMappings(mappingsData);
      }
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!email || !chatId) return;

    // Client-side validations
    if (!email.includes('@')) {
      setResult({ success: false, message: t('mappings_error_invalid') });
      return;
    }

    if (!/^-?\d+$/.test(chatId.trim())) {
      setResult({ success: false, message: t('mappings_error_invalid') });
      return;
    }

    setSubmitting(true);
    setResult(null);

    try {
      const res = await apiFetch(`${API_BASE}/api/mappings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), chat_id: chatId.trim() }),
      });
      const data = await res.json();
      
      if (res.ok && data.success) {
        setResult({ success: true, message: t('mappings_success_add') });
        setEmail('');
        setChatId('');
        fetchData();
      } else {
        setResult({ success: false, message: data.detail || t('send_server_error') });
      }
    } catch {
      setResult({ success: false, message: t('send_server_error') });
    } finally {
      setSubmitting(false);
      setTimeout(() => setResult(null), 4000);
    }
  };

  const handleDelete = async (emailToDelete) => {
    setDeletingEmail(emailToDelete);
    try {
      const res = await apiFetch(`${API_BASE}/api/mappings?email=${encodeURIComponent(emailToDelete)}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult({ success: true, message: t('mappings_success_delete') });
        fetchData();
      } else {
        setResult({ success: false, message: data.detail || t('send_server_error') });
      }
    } catch {
      setResult({ success: false, message: t('send_server_error') });
    } finally {
      setDeletingEmail(null);
      setTimeout(() => setResult(null), 4000);
    }
  };

  const handleUseUser = (user) => {
    setChatId(String(user.id));
    document.getElementById('email-input')?.focus();
  };

  const handleExport = async () => {
    try {
      const res = await apiFetch(`${API_BASE}/api/mappings/export`);
      if (!res.ok) {
        setResult({ success: false, message: "Export qilishda xatolik" });
        setTimeout(() => setResult(null), 4000);
        return;
      }
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mappings_backup_${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setResult({ success: true, message: "Ma'lumotlar muvaffaqiyatli eksport qilindi" });
      setTimeout(() => setResult(null), 4000);
    } catch {
      setResult({ success: false, message: t('send_server_error') });
      setTimeout(() => setResult(null), 4000);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    setResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await apiFetch(`${API_BASE}/api/mappings/import`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      
      if (res.ok && data.success) {
        setResult({ success: true, message: data.message });
        fetchData();
      } else {
        setResult({ success: false, message: data.detail || "Import qilishda xatolik" });
      }
    } catch {
      setResult({ success: false, message: t('send_server_error') });
    } finally {
      setImporting(false);
      if (importInputRef.current) importInputRef.current.value = '';
      setTimeout(() => setResult(null), 4000);
    }
  };

  return (
    <div className="screen-enter pb-24 space-y-5">
      <div className="glass-card p-6 relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-cyan-500/15 rounded-full blur-2xl" />

        <div className="relative z-10 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold gradient-text">{t('mappings_title')}</h1>
            <p className="text-xs text-white/40 font-medium">{t('mappings_subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <button 
              type="button"
              onClick={handleExport}
              className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors active:scale-95"
              title="Export"
            >
              <Download size={16} className="text-emerald-400" />
            </button>
            <button 
              type="button"
              onClick={() => importInputRef.current?.click()}
              disabled={importing}
              className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors active:scale-95 disabled:opacity-50"
              title="Import"
            >
              {importing ? (
                <div className="w-4 h-4 border-2 border-white/10 border-t-blue-500 rounded-full animate-spin" />
              ) : (
                <Upload size={16} className="text-blue-400" />
              )}
            </button>
            <input
              ref={importInputRef}
              type="file"
              accept=".json"
              onChange={handleImport}
              className="hidden"
            />
            <button 
              type="button"
              onClick={fetchData} 
              disabled={loading || loadingUsers}
              className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors active:scale-95 disabled:opacity-50"
            >
              <RefreshCw size={16} className={`text-white/60 ${loading || loadingUsers ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Result feedback */}
      {result && (
        <div className={`
          glass-card p-4 flex items-center gap-3 animate-fade-in-up
          ${result.success ? 'border-emerald-500/20 text-emerald-400' : 'border-red-500/20 text-red-400'}
        `}>
          {result.success ? (
            <CheckCircle2 size={20} className="flex-shrink-0" />
          ) : (
            <AlertCircle size={20} className="flex-shrink-0" />
          )}
          <p className="text-sm font-medium">{result.message}</p>
        </div>
      )}

      {/* Add New Mapping Form */}
      <form onSubmit={handleAdd} className="glass-card p-5 space-y-4 animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
        <h3 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">
          {t('mappings_add')}
        </h3>
        
        <div className="space-y-3">
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30">
              <Mail size={16} />
            </span>
            <input
              id="email-input"
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('mappings_placeholder_email')}
              className="w-full pl-11"
              required
              disabled={submitting}
            />
          </div>

          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30">
              <Key size={16} />
            </span>
            <input
              id="chat-id-input"
              type="text"
              value={chatId}
              onChange={(e) => setChatId(e.target.value)}
              placeholder={t('mappings_placeholder_chat_id')}
              className="w-full pl-11"
              required
              disabled={submitting}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting || !email || !chatId}
          className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              <Plus size={16} />
              <span>{t('mappings_save')}</span>
            </>
          )}
        </button>
      </form>

      {/* Mappings List */}
      <div className="glass-card p-5 animate-fade-in-up stagger-2" style={{ opacity: 0 }}>
        <h3 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-3">
          {t('nav_mappings')} ({mappings.length})
        </h3>

        {/* Qidiruv */}
        {mappings.length > 0 && (
          <div className="relative mb-4">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30">
              <Search size={14} />
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Qidirish (email yoki chat ID)..."
              className="w-full pl-9 py-2 text-xs"
            />
          </div>
        )}

        {loading ? (
          <MappingSkeleton />
        ) : mappings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
            <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center">
              <Mail size={22} className="text-white/30" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white/60">{t('mappings_no_data')}</p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {mappings
              .filter(item => {
                if (!searchQuery.trim()) return true;
                const q = searchQuery.toLowerCase();
                return item.email.toLowerCase().includes(q) || String(item.chat_id).includes(q);
              })
              .map((item, index) => (
              <div key={index} className="py-3 flex items-center justify-between first:pt-0 last:pb-0 group animate-fade-in">
                <div className="min-w-0 pr-3">
                  <p className="text-sm font-medium text-white/80 truncate font-mono">{item.email}</p>
                  <p className="text-xs text-white/40 mt-0.5 font-mono">Chat ID: {item.chat_id}</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleDelete(item.email)}
                  disabled={deletingEmail === item.email}
                  className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center hover:bg-red-500/20 transition-colors active:scale-95 disabled:opacity-50"
                  title={t('mappings_delete')}
                >
                  {deletingEmail === item.email ? (
                    <div className="w-4 h-4 border-2 border-red-500/30 border-t-red-500 rounded-full animate-spin" />
                  ) : (
                    <Trash2 size={14} className="text-red-400" />
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bot Users List */}
      <div className="glass-card p-5 animate-fade-in-up stagger-3" style={{ opacity: 0 }}>
        <h3 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1">
          {t('mappings_users_title')} ({users.length})
        </h3>
        <p className="text-[10px] text-white/30 font-medium mb-4">{t('mappings_users_subtitle')}</p>

        {loadingUsers ? (
          <MappingSkeleton />
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
            <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center">
              <UserCheck size={22} className="text-white/30" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white/60">{t('mappings_users_no_data')}</p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-white/5 max-h-[300px] overflow-y-auto pr-1">
            {users.map((user, index) => {
              const name = `${user.first_name} ${user.last_name}`.trim() || 'Foydalanuvchi';
              return (
                <div key={index} className="py-3 flex items-center justify-between first:pt-0 last:pb-0 animate-fade-in">
                  <div className="min-w-0 pr-3">
                    <p className="text-sm font-medium text-white/80 truncate">
                      {name} {user.username && <span className="text-xs text-blue-400 font-mono">@{user.username}</span>}
                    </p>
                    <p className="text-xs text-white/40 mt-0.5 font-mono">
                      ID: {user.id} {user.last_seen && <span className="text-[9px] text-white/20 ml-2">({user.last_seen})</span>}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleUseUser(user)}
                    className="px-3 py-1.5 rounded-lg bg-blue-500/10 text-xs font-semibold text-blue-400 hover:bg-blue-500/20 transition-all duration-300 active:scale-95 flex-shrink-0"
                  >
                    {t('mappings_use_id')}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Mappings;
